/**
 * The People page's boxes (P03 to P07): the register List with its figures in
 * its head under its read (R5), its find and filters (R6) and its selection;
 * Send fresh setup emails, the scoped bulk Task over that selection (R6, W1,
 * CASE-029); Add teacher; and the institution's managed Google accounts as
 * their own Fact with their own read (R5, C4).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";

import { useTabDraft, useTabDrafts } from "@/components/ui/box-draft";
import { selectionOf, type Blocker, type Box, type FactBox, type ListBox, type ListFacet, type Read, type Result, type TaskBox } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { MANAGED_IDENTITY_SUMMARY_QUERY_KEY, accountsApi, type ManagedIdentitySummary } from "@/lib/api/accounts";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { checkCommit, decodeBulkResult, type BulkResult } from "@/lib/api/commitSeams";
import { invitationsApi } from "@/lib/api/invitations";
import {
  PEOPLE_QUERY_KEY,
  peopleApi,
  type PeopleCurrentInvitation,
  type PeopleRegisterFilters,
  type PeopleRegisterResponse,
  type PeopleRegisterRow,
} from "@/lib/api/people";
import { personName } from "@/lib/person-name";

import { invitationWord, resendable, resultState } from "../visit/access-boxes";
import { imam, queryRead, type PendingCommit, refusalSentence, useTaskCommit } from "../visit/commit";
import { RELATIONSHIP_LABEL } from "../person-record";
import { registerCopy as copy, registerText as text } from "./register-copy";

const box = <T,>(spec: FactBox<T> | ListBox<T, unknown> | TaskBox<T>) => spec as unknown as Box;
const effectsOf = (sentences: string[]) => () => sentences.map((sentence, index) => ({ id: `effect-${index}`, sentence }));

export const registerQueryKey = (filters: PeopleRegisterFilters) => [...PEOPLE_QUERY_KEY, "register", filters] as const;

/** A person the register's selection holds, kept as read so a hidden row still names its invitation. */
export interface Selected {
  key: string;
  name: string;
  invitation: PeopleCurrentInvitation | null;
  signsIn: boolean;
  archived: boolean;
}

export const selectedOf = (row: PeopleRegisterRow): Selected => ({
  key: row.person_id,
  name: personName(row.display_name),
  invitation: row.current_invitation ?? null,
  signsIn: row.user_id !== null && row.role !== null,
  archived: Boolean(row.archived_at),
});

const included = (person: Selected) => !person.archived && !person.signsIn && resendable(person.invitation);
// The true reason a selected person is left out; signing in comes first, so an invitation they still carry is never denied.
const leftOutReason = (person: Selected) =>
  person.archived ? "restore to People first" : person.signsIn ? copy.leftOutSignsIn : person.invitation ? invitationWord(person.invitation).word : copy.leftOutNoInvitation;

/* ------------------------------------------------------------------ */
/* The register                                                        */
/* ------------------------------------------------------------------ */

export interface RegisterTools {
  filters: PeopleRegisterFilters;
  facets: ListFacet[];
  onQuery: (query: string) => void;
}

export function accessWord(row: PeopleRegisterRow): string {
  if (row.retirement) return copy.accountRetired;
  if (row.role === null) return copy.noLmsAccount;
  return row.is_active ? copy.accessActive : copy.accessWithdrawn;
}

export function useRegister(tools: RegisterTools, selection: Map<string, Selected>, toggle: (row: PeopleRegisterRow, on: boolean) => void) {
  const query = useQuery({ queryKey: registerQueryKey(tools.filters), queryFn: () => peopleApi.list(tools.filters), placeholderData: (previous) => previous });
  // The Invite to sign in drafts the person visit keeps (box "person-invite"), read by record; any kept one counts.
  const inviteDrafts = useTabDrafts<unknown>("person-invite");
  const inviteKept = (row: PeopleRegisterRow) => inviteDrafts({ record: `person:${row.person_id}`, version: null, initial: null }).kept;
  const read: Read<PeopleRegisterResponse> = (() => {
    const base = queryRead(query, copy.registerUnavailable, copy.registerUnavailableDetail);
    if (base.status === "ready" && base.value.total === 0) {
      const filtered = Object.entries(tools.filters).some(([name, value]) => !["page", "page_size", "view"].includes(name) && (Array.isArray(value) ? value.length > 0 : Boolean(value)));
      return filtered || tools.filters.view !== "operational"
        ? { status: "empty", sentence: copy.noMatch, sub: copy.noMatchSub }
        : { status: "empty", sentence: copy.noPeople, sub: copy.noPeopleSub };
    }
    return base;
  })();
  const term = tools.facets.find((facet) => facet.label === copy.term);
  const scope = tools.filters.semester_id ? text.scope(term?.options.find((option) => option.value === tools.filters.semester_id)?.label ?? copy.term) : copy.allTerms;

  const register = box<PeopleRegisterResponse>({
    id: "register",
    kind: "list",
    job: copy.registerJob,
    label: copy.registerLabel,
    owner: imam,
    fold: "never",
    read,
    figures: (value) => [{ value: value.total, unit: value.total === 1 ? copy.matchingPerson : copy.matchingPeople }],
    detail: () => scope,
    state: (value) => ({ class: "quiet", word: value.rows.length < value.total ? text.shown(value.rows.length) : copy.allShown }),
    rows: (value) => value.rows,
    find: { placeholder: copy.find, onQueryChange: tools.onQuery, facets: tools.facets },
    row: {
      key: (row) => row.person_id,
      title: (row) => personName(row.display_name),
      // A person the registrar has not named yet is told apart by their email (ADR 0034).
      facts: (row) =>
        [row.display_name?.trim() ? null : row.email, row.sid, row.relationships.map((relationship) => RELATIONSHIP_LABEL[relationship]).join(", "), row.program_name, accessWord(row)]
          .filter(Boolean)
          .join(" · "),
      // Only a row whose invitation needs work is marked; its word is the act that visits that work (O10).
      // An Invite begun in their visit is said here too, so Return or a replaced visit never hides it (visit-draft-kept).
      state: (row) => {
        if (inviteKept(row)) return { class: "draft", word: copy.draftKept };
        const invitation = row.current_invitation;
        if (row.role !== null || !invitation) return null;
        const word = invitationWord(invitation);
        return word.class === "waiting" ? null : word;
      },
      act: (row) => {
        const invitation = row.current_invitation;
        const word = row.role === null && invitation ? invitationWord(invitation) : null;
        return word?.class === "waiting"
          ? { does: "visit", verb: word.word, visit: `person:${row.person_id}/access` }
          : { does: "visit", verb: copy.openPerson, visit: `person:${row.person_id}` };
      },
      controls: (row) => (
        <label className="flex min-h-11 items-center gap-2 text-ui">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            aria-label={text.selectPerson(personName(row.display_name))}
            checked={selection.has(row.person_id)}
            onChange={(event) => toggle(row, event.target.checked)}
          />
        </label>
      ),
    },
  } as ListBox<PeopleRegisterResponse, PeopleRegisterRow>);
  return { register, query, read };
}

/* ------------------------------------------------------------------ */
/* Send fresh setup emails                                             */
/* ------------------------------------------------------------------ */

export function useBulkSend(register: Box, shown: readonly PeopleRegisterRow[], selection: Map<string, Selected>, setSelection: (next: Map<string, Selected>) => void) {
  const client = useQueryClient();
  const people = [...selection.values()];
  const sendable = people.filter(included);
  const leftOut = people.filter((person) => !included(person));
  const shownKeys = new Set(shown.map((row) => row.person_id));
  const hidden = people.filter((person) => !shownKeys.has(person.key)).length;
  const byInvitation = new Map(sendable.map((person) => [person.invitation!.id, person]));
  const nameOf = (id: string) => byInvitation.get(id)?.name ?? sent.current.get(id) ?? id;
  // The names a send carried, so its result still names them after the selection narrows or clears.
  const sent = useRef(new Map<string, string>());

  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["invitations"] }),
    client.invalidateQueries({ queryKey: ["dashboard", "admin"] }),
  ]);
  const commit = useTaskCommit({
    subject: "people:send-setup-emails",
    words: { refused: copy.bulkRefused, unknown: copy.bulkUnknown, unknownSentence: copy.bulkUnknownSentence, checkNote: copy.bulkCheckNote },
    refresh,
  });
  const [checked, setChecked] = useState<Result | null>(null);

  /** One bulk answer as the box's result; the selection clears on confirmed and narrows to the failed on partial (R6). */
  const answer = (bulk: BulkResult, key: string, effects: string[]): Result => {
    const retryable = bulk.failed.filter((outcome) => outcome.reason.fix?.target !== "check");
    if (bulk.state === "confirmed") {
      setSelection(new Map());
      return { state: "confirmed", cap: text.sentCount(bulk.succeeded.length), at: new Date().toISOString(), effects: bulk.succeeded.map((outcome) => text.sentTo(nameOf(outcome.id))), remains: [] };
    }
    const failedPeople = bulk.failed.map((outcome) => byInvitation.get(outcome.id)).filter((person): person is Selected => Boolean(person));
    setSelection(new Map(failedPeople.map((person) => [person.key, person])));
    if (!retryable.length) return commit.unknown(key, effects);
    return {
      state: "partial",
      cap: text.sentOf(bulk.succeeded.length, bulk.succeeded.length + bulk.failed.length),
      at: new Date().toISOString(),
      went: bulk.succeeded.map((outcome) => ({ sentence: text.sentTo(nameOf(outcome.id)), ids: [outcome.id] })),
      failed: bulk.failed.map((outcome) => ({
        sentence: `${nameOf(outcome.id)}: ${outcome.reason.fix?.target === "check" ? copy.checkInvitation : outcome.reason.message}`,
        ids: [outcome.id],
      })),
      requestId: key,
      retry: {
        verb: text.retry(retryable.length),
        note: copy.retryNote,
        run: async () => {
          let next: Result;
          try {
            next = answer(decodeBulkResult(await invitationsApi.sendSetupEmails(retryable.map((outcome) => outcome.id), key, true)), key, effects);
          } catch (error) {
            next = { state: "refused", cap: copy.bulkRefused, sentence: refusalSentence(error, copy.bulkRefused), draft: "kept" };
          }
          await refresh().catch(() => undefined);
          setChecked(next);
          return next;
        },
      },
    };
  };

  // A new selection after a confirmed send starts a new send; the last result has done its work.
  const confirmedAndMoved = people.length > 0 && (checked ?? commit.result).state === "confirmed";
  useEffect(() => {
    if (!confirmedAndMoved) return;
    setChecked(null);
    commit.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedAndMoved]);

  const ids = sendable.map((person) => person.invitation!.id);
  const effects = sendable.map((person) => text.sendTo(person.name));
  const result = checked ?? commit.result;
  const scope = selectionOf(register as ListBox<unknown, unknown>, people.map((person) => person.key), hidden);
  const blockers = (): Blocker[] =>
    people.length && !sendable.length ? [{ kind: "missing", part: "bulk-included", sentence: text.noneSelectedHaveOne(people.length) }] : [];
  const clear = (
    <button type="button" className="text-ui text-primary underline-offset-4 hover:underline" onClick={() => setSelection(new Map())}>
      {copy.clearSelection}
    </button>
  );
  const rows = (list: Selected[], reason?: (person: Selected) => string) => (
    <ul className="grid gap-1 text-ui">
      {list.map((person) => (
        <li key={person.key} className="[overflow-wrap:anywhere]">
          {person.name}
          <span className="text-muted-foreground"> · {reason ? reason(person) : person.invitation ? invitationWord(person.invitation).word : copy.leftOutPart}</span>
        </li>
      ))}
    </ul>
  );

  const empty = people.length === 0 && result.state === "none";
  if (empty) return null;
  return box<null>({
    id: "bulk",
    kind: "task",
    job: copy.bulkJob,
    label: copy.bulkLabel,
    owner: imam,
    fold: "choice",
    read: { status: "ready", value: null, readAt: new Date(0).toISOString() },
    detail: () => (empty ? copy.bulkNothingDetail : text.withAndWithout(sendable.length, leftOut.length)),
    state: () =>
      empty
        ? { class: "quiet", word: copy.bulkNothing }
        : resultState(result, { class: "waiting", word: text.selected(people.length, hidden) }, { class: "done", word: copy.sent }),
    openState: () =>
      resultState(result, sendable.length ? { class: "draft", word: copy.readyToSend } : { class: "waiting", word: copy.nobodyToSend }, { class: "done", word: copy.sent }),
    ...(empty ? {} : { act: { does: "open" as const, verb: text.review(people.length) } }),
    candidate: empty ? undefined : { eligible: true, blocked: null, rank: null },
    parts: [
      {
        id: "bulk-included",
        label: copy.included,
        state: sendable.length ? { class: "draft", word: String(sendable.length) } : { class: "waiting", word: "0" },
        owner: imam,
        fold: "never",
        draft: "none",
        render: () => (
          <div className="grid gap-2">
            {sendable.length ? rows(sendable) : <p className="text-ui text-muted-foreground">{copy.nobodyIncluded}</p>}
            {people.length > 0 && <div>{clear}</div>}
          </div>
        ),
      },
      ...(leftOut.length
        ? [{
            id: "bulk-left-out",
            label: copy.leftOutPart,
            state: { class: "quiet" as const, word: String(leftOut.length) },
            owner: imam,
            fold: "never" as const,
            draft: "none" as const,
            render: () => rows(leftOut, leftOutReason),
          }]
        : []),
    ],
    over: { selection: scope, leftOut: leftOut.map((person) => ({ id: person.key, sentence: text.leaveOut(person.name, leftOutReason(person)) })) },
    commit: {
      verb: text.send(sendable.length),
      cap: copy.bulkCap,
      effects: effectsOf(effects),
      blockers,
      note: copy.bulkNote,
      run: async (key) => {
        setChecked(null);
        for (const person of sendable) sent.current.set(person.invitation!.id, person.name);
        const next = await commit.run(key, {
          effects,
          write: async (commitKey) => answer(decodeBulkResult(await invitationsApi.sendSetupEmails(ids, commitKey)), commitKey, effects),
        });
        if (next.state !== "unknown") return next;
        // Check reads the receipt's projection; a bulk answer comes back as one.
        const checkable: Result = {
          ...next,
          check: {
            ...next.check,
            run: async () => {
              const read = await checkCommit(key, decodeBulkResult).catch(() => null);
              let done: Result = next;
              if (read?.state === "confirmed" || read?.state === "partial") done = answer(read.receipt.result, key, effects);
              else if (read?.state === "not_committed" || read?.state === "refused")
                done = { state: "refused", cap: copy.bulkRefused, sentence: copy.bulkUnknownSentence, draft: "kept" };
              await refresh().catch(() => undefined);
              setChecked(done);
              return done;
            },
          },
        };
        setChecked(checkable);
        return checkable;
      },
    },
    result,
    draft: false,
  });
}

/* ------------------------------------------------------------------ */
/* Add teacher                                                         */
/* ------------------------------------------------------------------ */

export function useBulkVisibility(selection: Map<string, Selected>, setSelection: (next: Map<string, Selected>) => void, shown: readonly PeopleRegisterRow[]) {
  const client = useQueryClient();
  const draft = useTabDraft<{ archived: boolean }, PendingCommit>({ box: "people-bulk-visibility", record: "selection", version: null, initial: { archived: true } });
  const selected = [...selection.values()];
  const archived = draft.kept || draft.pending ? draft.value.archived : selected.some(person => !person.archived);
  const changing = selected.filter(person => person.archived !== archived);
  const unchanged = selected.filter(person => person.archived === archived);
  const hidden = selected.filter(person => !shown.some(row => row.person_id === person.key)).length;
  const verb = archived ? "Archive selected" : "Restore selected";
  const settledEffects = (changed: number, total: number, target: boolean) => [
    `${target ? "Archived" : "Restored"} ${changed} ${changed === 1 ? "person" : "people"}`,
    ...(total > changed ? [`${total - changed} already ${target ? "archived" : "shown in People"}`] : []),
    "Sign-in and academic records kept as they are",
  ];
  const commit = useTaskCommit({
    subject: "people:bulk-visibility", keep: draft,
    words: { refused: "The selected people were not changed.", unknown: "Visibility change needs confirmation", unknownSentence: "We could not confirm this change. Check its saved result before trying again.", checkNote: "Reads the saved result. Nothing is changed again." },
    refresh: () => client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    onReceipt: () => setSelection(new Map()),
    checkBy: async key => {
      const checked = await checkCommit(key);
      if (checked.state !== "confirmed") return checked.state === "not_committed" ? "not_committed" : "unknown";
      const receipt = checked.receipt.result as { changed: number; person_ids: string[]; archived: boolean };
      setSelection(new Map());
      return { state: "confirmed", at: new Date().toISOString(), effects: settledEffects(receipt.changed, receipt.person_ids.length, receipt.archived), remains: [] };
    },
  });
  const effects = [`${archived ? "Archive" : "Restore"} ${changing.length} selected ${changing.length === 1 ? "person" : "people"}`, ...(unchanged.length ? [`Leave ${unchanged.length} already ${archived ? "archived" : "shown in People"}`] : []), "Keep sign-in access, academic relationships and historical records as they are"];
  const selectionMoved = selected.length > 0 && commit.result.state === "confirmed";
  useEffect(() => {
    if (selectionMoved) { commit.reset(); draft.discard(); }
    // A new selection starts new work; unknown results retain their original receipt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMoved]);
  return box<null>({
    id: "people-bulk-visibility", kind: "task", label: "Archive or restore selected", job: "Change the selected people's visibility together.", owner: imam,
    fold: "choice", read: { status: "ready", value: null, readAt: new Date(0).toISOString() },
    detail: () => text.selected(selected.length, hidden),
    state: () => resultState(commit.result, { class: "quiet", word: `${changing.length} to ${archived ? "archive" : "restore"}` }),
    act: { does: "open", verb: "Review selected people" }, candidate: { eligible: selected.length > 0, blocked: null, rank: null },
    parts: [{ id: "visibility-selection", label: "Selected people", owner: imam, fold: "never", state: { class: "quiet", word: String(selected.length) }, draft: "browser", keep: draft,
      render: () => <div className="grid gap-3"><fieldset className="flex flex-wrap gap-4"><legend className="mb-2 text-ui">Visibility change</legend>{[true, false].filter(target => selected.some(person => person.archived !== target)).map(target => <label key={String(target)} className="flex min-h-11 items-center gap-2 text-ui"><input type="radio" name="bulk-visibility" checked={archived === target} onChange={() => { commit.reset(); draft.set({ archived: target }); }} />{target ? "Archive" : "Restore to People"}</label>)}</fieldset><ul className="list-disc pl-5 text-ui">{changing.map(person => <li key={person.key}>{person.name}</li>)}{unchanged.map(person => <li key={person.key} className="text-muted-foreground">{person.name}: already {archived ? "archived" : "shown in People"}</li>)}</ul></div>,
    }],
    commit: { verb, cap: "When you confirm", effects: effectsOf(effects), blockers: () => changing.length ? [] : [{ kind: "missing", part: "visibility-selection", sentence: "No selected person needs this change." }],
      run: key => commit.run(key, { effects, write: async commitKey => { draft.set({ archived }); const answer = await peopleApi.setArchivedMany(selected.map(person => person.key), archived, commitKey); setSelection(new Map()); return commit.confirmed(settledEffects(answer.changed, answer.person_ids.length, archived)); } }),
    }, result: commit.result, draft: draft.kept || commit.pending,
  });
}

export function useAddTeacher(register: Read<PeopleRegisterResponse>, teacherScope = false) {
  const client = useQueryClient();
  // Keep the old draft address so an unconfirmed teacher creation can still be checked.
  const draft = useTabDraft<{ name: string; email?: string; relationship?: "person" | "teacher" | "student" }, PendingCommit>({ box: "add-teacher", record: "new", version: null, initial: { name: "", email: "", relationship: teacherScope ? "teacher" : "person" } });
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["faculty"] }),
  ]);
  const commit = useTaskCommit({
    words: { refused: copy.addRefused, unknown: copy.addUnknown, unknownSentence: copy.addUnknownSentence, checkNote: copy.addCheckNote },
    refresh,
    subject: "people:add-person",
    keep: draft,
    // Check reads the receipt of the key the unknown result carries; a confirmed one names the new person.
    onReceipt: (result) => {
      const personId = (result as { person_id?: string }).person_id;
      if (personId) setCreated({ id: personId, name: draft.value.name.trim() });
    },
  });
  const name = draft.value.name.trim();
  const student = draft.value.relationship === "student";
  const ranking = register.status === "ready" ? register.value.ranking : undefined;
  const rank = ranking?.state === "ready" ? (ranking.value.candidates.find((candidate) => candidate.id === "add")?.rank ?? null) : null;
  const effects = [copy.addEffect, copy.addShows];
  const done = commit.result.state === "confirmed";
  return box<PeopleRegisterResponse>({
    id: "add",
    kind: "task",
    job: copy.addJob,
    label: copy.addLabel,
    owner: imam,
    fold: "choice",
    read: register.status === "empty" ? { status: "ready", value: { rows: [], page: 1, page_size: 25, total: 0, semester_id: null, view: "operational" }, readAt: new Date().toISOString() } : register,
    detail: () => copy.addDetail,
    openDetail: () => (done ? undefined : student ? "New students start with an admitted application or a continuing-student intake." : copy.addOpenDetail),
    state: () => {
      const resting = draft.kept
        ? { class: "draft" as const, word: name ? copy.readyToAdd : copy.nameNeeded }
        : { class: "quiet" as const, word: "Name and contact" };
      return resultState(commit.result, resting, { class: "done", word: copy.added });
    },
    act: done && created ? { does: "visit", verb: text.open(created.name), visit: `person:${created.id}` } : { does: "open", verb: copy.addLabel },
    // The confirmed result exits to the new record from the open box too (add-teacher-created).
    exits: done && created ? [{ label: text.open(created.name), visit: `person:${created.id}` }, { label: "Set up sign-in", visit: `person:${created.id}/access` }] : undefined,
    candidate: { eligible: !done, blocked: null, rank },
    parts: [{
      id: "add-who",
      label: copy.who,
      state: name ? { class: "done", word: copy.named } : { class: "waiting", word: copy.nameNeeded },
      owner: imam,
      fold: "never",
      draft: "browser",
      keep: draft,
      render: () => (
        <div className="grid gap-1.5">
          <Label htmlFor="add-person-relationship">Record type</Label>
          <NativeSelect id="add-person-relationship" value={draft.value.relationship ?? "teacher"} onChange={event => {
            commit.reset();
            setCreated(null);
            draft.set(current => ({ ...current, relationship: event.target.value as "person" | "teacher" | "student" }));
          }}>
            <option value="person">Person or staff member</option>
            <option value="teacher">Teacher</option>
          </NativeSelect>
          <div className="grid gap-2 py-2 text-meta text-muted-foreground"><p>Adding a student? Start from an admitted application or registrar intake. Manage their records and access here afterwards.</p><div className="flex flex-wrap gap-3"><Link className="underline" to="/admin/admissions">Open admissions</Link><Link className="underline" to="/admin/imports">Open registrar intake</Link></div></div>
          <Label htmlFor="add-teacher-name">{copy.name}</Label>
          <Input
            id="add-teacher-name"
            autoComplete="off"
            value={draft.value.name}
            onChange={(event) => {
              commit.reset();
              setCreated(null);
              draft.set(current => ({ ...current, name: event.target.value }));
            }}
          />
          <Label htmlFor="add-person-email">Contact email (optional)</Label>
          <Input id="add-person-email" type="email" autoComplete="off" value={draft.value.email ?? ""} onChange={event => {
            commit.reset();
            draft.set(current => ({ ...current, email: event.target.value }));
          }} />
          <p className="text-meta text-muted-foreground">No sign-in is created and no email is sent. Manage access from the new person's record.</p>
        </div>
      ),
    }],
    commit: {
      verb: copy.addLabel,
      cap: copy.addCap,
      effects: effectsOf(effects),
      blockers: () => (student ? [{ kind: "missing", part: "add-who", sentence: "Open admissions or registrar intake to create a student record." }] : name ? [] : [{ kind: "missing", part: "add-who", sentence: "Enter the person's name." }]),
      run: (key) =>
        commit.run(key, {
          effects: [text.addedName(name)],
          write: async (commitKey) => {
            const made = await peopleApi.create({ full_name: name, email: draft.value.email?.trim() || null, relationship: (draft.value.relationship ?? "teacher") === "teacher" ? "teacher" : "person" }, commitKey);
            setCreated({ id: made.person_id, name });
          },
        }),
    },
    result: commit.result,
    draft: draft.kept,
  });
}

/* ------------------------------------------------------------------ */
/* Institution-managed Google accounts                                 */
/* ------------------------------------------------------------------ */

export function useManagedAccounts(): Box | null {
  const query = useQuery({ queryKey: MANAGED_IDENTITY_SUMMARY_QUERY_KEY, queryFn: () => accountsApi.managedIdentitySummary(), retry: false });
  // An institution with no managed accounts has nothing to count: a row of zeroes would claim a Workspace boundary it lacks.
  if (query.data && query.data.managed === 0) return null;
  if (query.isPending) return null;
  return box<ManagedIdentitySummary>({
    id: "managed",
    kind: "fact",
    job: copy.managedJob,
    label: copy.managedLabel,
    owner: imam,
    fold: "choice",
    readTime: true,
    read: queryRead(query, copy.managedUnavailable),
    figures: (value) => [
      { value: value.managed, unit: copy.managed },
      { value: value.suspended, unit: copy.suspended },
      { value: value.incomplete, unit: copy.notConverged },
      { value: value.unresolved, unit: copy.unresolved },
    ],
    detail: () => copy.managedDetail,
  });
}

/* ------------------------------------------------------------------ */
/* The filters' own reads                                              */
/* ------------------------------------------------------------------ */

export function useFilterChoices(enabled: { cohorts: boolean }) {
  const programs = useQuery({ queryKey: ["programs"], queryFn: () => academicStructureApi.getPrograms() });
  const cohorts = useQuery({ queryKey: ["cohorts", "all"], queryFn: () => academicStructureApi.getCohorts({ status: "all" }), enabled: enabled.cohorts });
  const semesters = useQuery({ queryKey: ["semesters"], queryFn: () => academicStructureApi.getSemesters() });
  return { programs, cohorts, semesters };
}

/** A filter's choices as its read says them: waiting while it reads, and Try again if it fails (R1). */
export function choicesRead(query: { isPending: boolean; isError: boolean; refetch: () => unknown }): ListFacet["read"] {
  if (query.isError) return { status: "failed", sentence: copy.optionsUnavailable, retry: () => void query.refetch() };
  if (query.isPending) return { status: "loading" };
  return undefined;
}

/** The register's selection: its own, kept across filtering and paging until a send confirms or it is cleared (R6). */
export function useSelection() {
  const [selection, setSelection] = useState<Map<string, Selected>>(() => new Map());
  const toggle = (row: PeopleRegisterRow, on: boolean) =>
    setSelection((current) => {
      const next = new Map(current);
      if (on) next.set(row.person_id, selectedOf(row));
      else next.delete(row.person_id);
      return next;
    });
  return { selection, setSelection, toggle };
}

/** A selected row read again keeps what it now says, such as a fresh invitation. */
export function useSelectionReads(rows: readonly PeopleRegisterRow[], setSelection: (update: (current: Map<string, Selected>) => Map<string, Selected>) => void) {
  useEffect(() => {
    setSelection((current) => {
      let changed = false;
      const next = new Map(current);
      for (const row of rows) {
        const was = next.get(row.person_id);
        if (!was) continue;
        const fresh = selectedOf(row);
        if (JSON.stringify(fresh) !== JSON.stringify(was)) {
          next.set(row.person_id, fresh);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rows, setSelection]);
}
